import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useAppContext } from "../store";
import { ACTION_TYPES } from "../store/actions";
import { ROUTES } from "../constants";
import BusinessCentralService from "../services/BusinessCentralService";
import { getUrlParam, clearSetupCache, loadSetupCache, saveSetupCache, markSessionStarted, hasActiveSession, findCaseInsensitiveMatch, isAutoConfirmSkipped, clearAutoConfirmSkip, skipAutoConfirmOnce, } from "../store/helpers";
import EnvironmentSelector from "../components/EnvironmentSelector";
import CompanySelector from "../components/CompanySelector";
import PosUnitSelector from "../components/PosUnitSelector";
import SummaryView, { actionButtonClassName } from "../components/SummaryView";
import Button from "../components/Button";
import { CircleSpinner } from "../components/CircleSpinner";
import { getIsCrane, getMsTenantFromUrl } from "../authConfig";
import { getTranslation } from "../translations";
const CLOUD_AUTO_START_SETTINGS = ["environment", "company", "pos"];
const CRANE_AUTO_START_SETTINGS = [
    "tenantId",
    "environment",
    "company",
    "BasicAuthToken",
    "pos",
];
const SetupSelector = ({ setReadyToRender }) => {
    const labels = getTranslation();
    const isCrane = getIsCrane();
    const { dispatch } = useAppContext();
    const [configuration, setConfiguration] = useState({
        environments: [],
        companies: [],
        posUnits: [],
        selectedEnvironment: null,
        selectedCompany: null,
        selectedPosUnit: null,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [urlParams] = useState(() => ({
        environment: getUrlParam("environment"),
        company: getUrlParam("company"),
        pos: getUrlParam("pos"),
        autoConfirm: getUrlParam("autoConfirm") === "true" && !isAutoConfirmSkipped(),
    }));
    const { instance } = useMsal();
    const [account, setAccount] = useState(() => instance.getActiveAccount());
    const MS_TENANT = isCrane ? "" : getMsTenantFromUrl() ?? "";
    useEffect(() => {
        // If URL params are provided, we'll match them case-insensitively after fetching data
        if (urlParams.environment || urlParams.company || urlParams.pos) {
            markSessionStarted();
            return;
        }
        if (!hasActiveSession()) {
            markSessionStarted();
            return;
        }
        const cached = loadSetupCache();
        if (cached) {
            setConfiguration((prev) => ({
                ...prev,
                selectedEnvironment: cached.environment ?? prev.selectedEnvironment,
                selectedCompany: cached.company ?? prev.selectedCompany,
                selectedPosUnit: cached.posUnit ?? prev.selectedPosUnit,
            }));
        }
    }, [urlParams]);
    useEffect(() => {
        const accounts = instance.getAllAccounts();
        const active = instance.getActiveAccount();
        if (!active && accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
            setAccount(accounts[0]);
        }
        else {
            setAccount(active);
        }
    }, [instance]);
    const handleConfirmation = useCallback(() => {
        const customWindow = window;
        customWindow.loginConfig = customWindow.loginConfig ?? {};
        if (isCrane) {
            const config = customWindow.loginConfig.onPremiseConfig;
            if (config) {
                config.posUnit = configuration.selectedPosUnit ?? "";
                dispatch({
                    type: ACTION_TYPES.SET_LOGIN_CONFIG,
                    payload: {
                        isOnCloud: false,
                        onPremiseConfig: {
                            tenantId: config.tenantId,
                            environment: config.environment,
                            company: config.company,
                            basicAuthToken: config.basicAuthToken,
                            posUnit: config.posUnit,
                        },
                    },
                });
            }
        }
        else {
            if (!MS_TENANT) {
                setError(labels.missingTenantInUrl);
                return;
            }
            customWindow.loginConfig.cloudConfig = {
                tenantId: MS_TENANT,
                environment: configuration.selectedEnvironment ?? "",
                company: configuration.selectedCompany ?? "",
                posUnit: configuration.selectedPosUnit ?? "",
            };
            dispatch({
                type: ACTION_TYPES.SET_LOGIN_CONFIG,
                payload: {
                    isOnCloud: true,
                    cloudConfig: {
                        tenantId: MS_TENANT,
                        environment: configuration.selectedEnvironment ?? "",
                        company: configuration.selectedCompany ?? "",
                        posUnit: configuration.selectedPosUnit ?? "",
                    },
                },
            });
        }
        clearAutoConfirmSkip();
        dispatch({ type: ACTION_TYPES.CHANGE_PAGE, payload: ROUTES.HOME_PAGE });
        setReadyToRender(true);
    }, [configuration, setReadyToRender, dispatch, MS_TENANT, labels]);
    useEffect(() => {
        // Only handle on-premise (Crane) setup with direct URL params here
        // Cloud setup uses case-insensitive matching after fetching from API
        const tenantId = getUrlParam("tenantId");
        const env = getUrlParam("environment");
        const company = getUrlParam("company");
        const basicAuthToken = getUrlParam("BasicAuthToken");
        if (tenantId && env && company && basicAuthToken) {
            const customWindow = window;
            customWindow.loginConfig = customWindow.loginConfig ?? {};
            customWindow.loginConfig.onPremiseConfig = {
                tenantId: tenantId,
                environment: env,
                company: company,
                basicAuthToken: basicAuthToken,
            };
            // For on-premise, set values directly (POS units will still be matched case-insensitively)
            setConfiguration((prev) => ({
                ...prev,
                selectedEnvironment: env,
                selectedCompany: company,
            }));
        }
    }, []);
    useEffect(() => {
        const isCrane = getUrlParam("crane") === "true";
        if (isCrane)
            return;
        if (account && configuration.environments.length === 0) {
            const fetchEnvironments = async () => {
                setLoading(true);
                try {
                    const bcService = new BusinessCentralService(instance);
                    const bcEnvList = await bcService.getEnvironments();
                    const envSelectList = bcEnvList.map((env) => ({
                        name: env.name,
                        type: env.type,
                    }));
                    // Case-insensitive match for URL param
                    const matchedEnv = findCaseInsensitiveMatch(envSelectList, urlParams.environment, (env) => env.name);
                    setConfiguration((prev) => ({
                        ...prev,
                        environments: envSelectList,
                        selectedEnvironment: matchedEnv?.name ?? prev.selectedEnvironment,
                    }));
                }
                catch (err) {
                    setError(labels.fetchEnvironmentsFailed);
                }
                finally {
                    setLoading(false);
                }
            };
            fetchEnvironments();
        }
    }, [
        account,
        instance,
        configuration.environments.length,
        urlParams.environment,
        labels,
    ]);
    useEffect(() => {
        const isCrane = getUrlParam("crane") === "true";
        if (isCrane)
            return;
        if (account &&
            configuration.selectedEnvironment &&
            configuration.companies.length === 0 &&
            !error) {
            const fetchCompanies = async () => {
                setLoading(true);
                const bcService = new BusinessCentralService(instance);
                if (configuration.selectedEnvironment !== null) {
                    try {
                        const bcCompanyList = await bcService.getCompanies(MS_TENANT, configuration.selectedEnvironment);
                        const companySelectList = bcCompanyList.map((company) => ({
                            id: company.id,
                            name: company.name,
                        }));
                        // Case-insensitive match for URL param
                        const matchedCompany = findCaseInsensitiveMatch(companySelectList, urlParams.company, (company) => company.name);
                        setConfiguration((prev) => ({
                            ...prev,
                            companies: companySelectList,
                            selectedCompany: matchedCompany?.name ?? prev.selectedCompany,
                        }));
                    }
                    catch (err) {
                        setError(labels.fetchCompaniesFailed);
                    }
                    finally {
                        setLoading(false);
                    }
                }
            };
            fetchCompanies();
        }
    }, [
        account,
        configuration.selectedEnvironment,
        configuration.companies.length,
        error,
        instance,
        urlParams.company,
        labels,
    ]);
    useEffect(() => {
        const customWindow = window;
        const shouldFetch = configuration.posUnits.length === 0 && !error;
        if (!shouldFetch)
            return;
        const fetchPosUnits = async () => {
            setLoading(true);
            try {
                let bcPosUnitList = [];
                if (isCrane) {
                    const { tenantId, environment, company, basicAuthToken } = customWindow.loginConfig?.onPremiseConfig ?? {};
                    if (!tenantId || !environment || !company || !basicAuthToken) {
                        throw new Error("Missing credentials");
                    }
                    const bcService = new BusinessCentralService(undefined);
                    bcPosUnitList = await bcService.getPosUnits(tenantId, environment, company, { isOnPremise: true, basicAuthToken });
                }
                else if (account &&
                    configuration.selectedEnvironment &&
                    configuration.selectedCompany) {
                    const bcService = new BusinessCentralService(instance);
                    bcPosUnitList = await bcService.getPosUnits(MS_TENANT, configuration.selectedEnvironment, configuration.selectedCompany);
                }
                else {
                    return;
                }
                const posUnitSelectList = bcPosUnitList.map((unit) => ({
                    code: unit.code,
                    id: unit.id,
                    name: unit.name,
                    posStoreCode: unit.posStoreCode,
                }));
                // Case-insensitive match for URL param
                const matchedPosUnit = findCaseInsensitiveMatch(posUnitSelectList, urlParams.pos, (unit) => unit.code);
                setConfiguration((prev) => ({
                    ...prev,
                    posUnits: posUnitSelectList,
                    selectedPosUnit: matchedPosUnit?.code ?? prev.selectedPosUnit,
                }));
            }
            catch (err) {
                setError(labels.fetchPosUnitsFailed);
            }
            finally {
                setLoading(false);
            }
        };
        fetchPosUnits();
    }, [
        account,
        configuration.selectedEnvironment,
        configuration.selectedCompany,
        configuration.posUnits.length,
        error,
        urlParams.pos,
        instance,
        labels,
    ]);
    // Auto-confirm when all values are matched and autoConfirm is enabled
    useEffect(() => {
        if (urlParams.autoConfirm &&
            configuration.selectedEnvironment &&
            configuration.selectedCompany &&
            configuration.selectedPosUnit &&
            !loading &&
            !error) {
            handleConfirmation();
        }
    }, [
        urlParams.autoConfirm,
        configuration.selectedEnvironment,
        configuration.selectedCompany,
        configuration.selectedPosUnit,
        loading,
        error,
        handleConfirmation,
    ]);
    const handleLogout = () => {
        clearSetupCache();
        sessionStorage.clear();
        skipAutoConfirmOnce();
        instance.logoutRedirect({
            account,
            postLogoutRedirectUri: window.location.href,
        });
    };
    const handleClear = () => {
        const isCrane = getUrlParam("crane") === "true";
        if (isCrane) {
            setConfiguration((prev) => ({
                ...prev,
                selectedPosUnit: null,
            }));
            saveSetupCache({ posUnit: undefined });
        }
        else {
            setConfiguration((prev) => ({
                ...prev,
                selectedEnvironment: null,
                selectedCompany: null,
                selectedPosUnit: null,
            }));
            clearSetupCache();
        }
    };
    const getAutoStartProblem = () => {
        if (!urlParams.autoConfirm)
            return null;
        const requiredSettings = isCrane
            ? CRANE_AUTO_START_SETTINGS
            : CLOUD_AUTO_START_SETTINGS;
        const isSettingMissing = (setting) => !getUrlParam(setting);
        const missingSettings = requiredSettings.filter(isSettingMissing);
        if (missingSettings.length > 0) {
            return labels.autoStartMissing(missingSettings.join(", "));
        }
        const notFoundProblem = (setting, urlValue) => labels.autoStartNotFound(setting, String(urlValue));
        const { environments, companies, posUnits, selectedEnvironment, selectedCompany, selectedPosUnit, } = configuration;
        if (selectedEnvironment === null) {
            return environments.length > 0
                ? notFoundProblem("environment", urlParams.environment)
                : null;
        }
        if (selectedCompany === null) {
            return companies.length > 0
                ? notFoundProblem("company", urlParams.company)
                : null;
        }
        return posUnits.length > 0 && selectedPosUnit === null
            ? notFoundProblem("pos", urlParams.pos)
            : null;
    };
    const autoStartProblem = getAutoStartProblem();
    const versionLabel = (_jsxs("small", { className: "fixed bottom-2 right-3 text-3xl text-[color:var(--color-grey-600)]", children: ["v", __APP_VERSION__] }));
    if (urlParams.autoConfirm && !error && !autoStartProblem) {
        return (_jsxs("div", { className: "flex items-center justify-center h-screen", children: [_jsx(CircleSpinner, {}), versionLabel] }));
    }
    return (_jsxs("div", { className: "flex flex-col items-center justify-start min-h-screen overflow-y-auto gap-1.5 px-4 py-8", children: [configuration.selectedEnvironment === null ||
                configuration.selectedCompany === null ||
                configuration.selectedPosUnit === null ? (_jsxs(_Fragment, { children: [configuration.selectedEnvironment === null ? (_jsx(EnvironmentSelector, { environments: configuration.environments, loading: loading, error: error, onSelect: (env) => {
                            setConfiguration((prev) => ({
                                ...prev,
                                selectedEnvironment: env,
                                selectedCompany: null,
                                selectedPosUnit: null,
                            }));
                            saveSetupCache({
                                environment: env,
                                company: undefined,
                                posUnit: undefined,
                            });
                        } })) : configuration.selectedCompany === null ? (_jsx(CompanySelector, { companies: configuration.companies, environment: configuration.selectedEnvironment, loading: loading, error: error, onSelect: (company) => {
                            setConfiguration((prev) => ({
                                ...prev,
                                selectedCompany: company,
                                selectedPosUnit: null,
                            }));
                            saveSetupCache({ company, posUnit: undefined });
                        } })) : configuration.selectedPosUnit === null ? (_jsx(PosUnitSelector, { posUnits: configuration.posUnits, environment: configuration.selectedEnvironment, company: configuration.selectedCompany, loading: loading, error: error, onSelect: (pos) => {
                            setConfiguration((prev) => ({ ...prev, selectedPosUnit: pos }));
                            saveSetupCache({ posUnit: pos });
                        } })) : null, autoStartProblem && (_jsx("p", { className: "mt-6 text-lg font-semibold text-blue-900 text-center", children: autoStartProblem })), error && !isCrane && (_jsx(Button, { onClick: handleLogout, className: `mt-6 ${actionButtonClassName}`, type: "button", children: labels.switchAccount }))] })) : (_jsx(SummaryView, { tenant: MS_TENANT, environment: configuration.selectedEnvironment, company: configuration.selectedCompany, posUnit: configuration.selectedPosUnit, onConfirm: handleConfirmation, onClear: handleClear, onLogout: handleLogout, onChangeEnvironment: () => {
                    setConfiguration((prev) => ({
                        ...prev,
                        selectedEnvironment: null,
                        selectedCompany: null,
                        selectedPosUnit: null,
                    }));
                    saveSetupCache({
                        environment: undefined,
                        company: undefined,
                        posUnit: undefined,
                    });
                }, onChangeCompany: () => {
                    setConfiguration((prev) => ({
                        ...prev,
                        selectedCompany: null,
                        selectedPosUnit: null,
                    }));
                    saveSetupCache({ company: undefined, posUnit: undefined });
                }, onChangePosUnit: () => {
                    setConfiguration((prev) => ({
                        ...prev,
                        selectedPosUnit: null,
                    }));
                    saveSetupCache({ posUnit: undefined });
                } })), versionLabel] }));
};
export default SetupSelector;
